import * as React from 'react';

import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@common/components/modal';
import { Button } from '@common/components/Button';
import { I18n } from 'react-redux-i18n';
import { HttpErrorDto } from '@common/dto/http';
import { getTestAttributes } from '@common/utils/tests';
import { Flex } from '@common/components/Flex';
import { Ellipsis } from '@common/components/Ellipsis';
import { FormControl } from '@common/components/form-control';

import './preview.styles.scss';

export type PreviewProps = {
  title: string;
  submitBtnProps?: {
    text?: React.ReactNode;
  };
  selectedFolder?: { name: string };
  close: () => void;
  onSubmit: (params: { name: string }) => Promise<void>;
  showErrorMessage: (msg: string) => void;
  onOpenFolderPicker: () => void;
  showNameField?: boolean;
};

export const Preview: React.FC<PreviewProps> = (props) => {
  const { title, close, submitBtnProps, onSubmit, showErrorMessage, onOpenFolderPicker, selectedFolder, showNameField = true } = props;
  const [submitting, setSubmitting] = React.useState(false);
  const [name, setName] = React.useState('');
  const moduleName = 'FolderPreviewManagement';

  const disableSubmitBtn = submitting || !selectedFolder || (showNameField ? !name.trim() : false);

  const handleSubmit = React.useCallback(async () => {
    try {
      setSubmitting(true);
      await onSubmit({ name: name.trim() });
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to save folder'))
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit, showErrorMessage, name]);

  const handleNameChange = React.useCallback((e: { target: { value: string } }) => {
    setName(e.target.value);
  }, []);

  return (
    <Modal
      show
      backdrop="static"
      bsClass="bootstrap modal"
      className="FolderPreviewManagementPreview"
      animation={false}
      onEscapeKeyDown={close}
      onHide={close}
    >
      <ModalHeader closeButton onHide={close}>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Flex alignItems="center">
          <span className="FolderPreviewManagementPreview__label">{I18n.t('Folder')}</span>
          <Flex alignItems="center">
            <Ellipsis 
              {...getTestAttributes(moduleName, 'folder-name', 'name')} 
              width={300} 
              className="FolderPreviewManagementPreview__folder"
              text={selectedFolder?.name} 
            />
            <Button 
              variant="text"
              {...getTestAttributes(moduleName, 'open-folder-picker', 'btn')} 
              className="FolderPreviewManagementPreview__open-btn" 
              onClick={onOpenFolderPicker}
            >
              <img src="gif/folder_open.gif" />
            </Button>
          </Flex>
        </Flex>

        {showNameField && (
          <Flex alignItems="center" className="FolderPreviewManagementPreview__section">
            <span className="FolderPreviewManagementPreview__label">{I18n.t('Name')}</span>
            <FormControl 
            onChange={handleNameChange} 
            className="FolderPreviewManagementPreview__text-field"
          />
          </Flex>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          disabled={submitting}
          {...getTestAttributes(moduleName, 'cancel', 'btn')}
          onClick={close}
        >
          {I18n.t('Cancel')}
        </Button>
        <Button
          {...getTestAttributes(moduleName, 'submit', 'btn')}
          disabled={disableSubmitBtn}
          bsStyle="primary"
          onClick={handleSubmit}
        >
          {submitBtnProps?.text ?? I18n.t('Create')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};